import { v4 as uuidv4 } from 'uuid'

import {
  createFileDetailNavState,
  describePresentationSlide,
  getViewportContextFromPanel,
  getViewportSelectionSummary,
  stripViewerSelection,
} from '@/components/chat-v2/lib/panelEntrypoints'
import { buildSelectionLocator } from '@/components/chat-v2/lib/selectionLocator'
import {
  readLatestViewerState,
  writeLatestViewerState,
} from '@/components/chat-v2/lib/viewerStateBridge'
import type {
  ChatEnvironmentStore,
  PastedTextCard,
} from '@/components/chat-v2/providers/ChatEnvironmentProvider'

import type {
  PresentationObjectSelection,
  PresentationViewerState,
  ViewportContext,
} from '@/types/chat-v2/sessions'
import type { UUID } from '@/types/Id'

export interface ViewportSelectionCardContent {
  /** File name + location, e.g. `sow.pdf — Pages 12–13`. */
  title: string
  /** The highlighted text, or a location description for non-text selections. */
  text: string
}

const pluralize = (value: string) => {
  if (value.endsWith('s')) return value
  if (value.endsWith('y')) return `${value.slice(0, -1)}ies`
  return `${value}s`
}

const describePresentationObjectGroups = (
  groups: readonly PresentationObjectSelection[],
) => {
  const grouped = new Map<
    string,
    { count: number; label: string; text?: string }
  >()
  for (const group of groups) {
    const label = (
      group.role && group.role !== 'object' ? group.role : group.node_kind
    ).replace(/[_-]+/g, ' ')
    const key = label.toLowerCase()
    const existing = grouped.get(key)
    const text = group.texts?.map((value) => value.trim()).find(Boolean)
    if (existing) {
      existing.count += group.count ?? 1
      existing.text ??= text
    } else {
      grouped.set(key, { count: group.count ?? 1, label, text })
    }
  }

  const descriptions = [...grouped.values()]
  const visible = descriptions.slice(0, 3).map(({ count, label, text }) => {
    const object =
      count === 1
        ? label.charAt(0).toUpperCase() + label.slice(1)
        : `${count} ${pluralize(label)}`
    return text ? `${object}: ${text}` : object
  })
  const hiddenCount = descriptions
    .slice(3)
    .reduce((total, group) => total + group.count, 0)
  if (hiddenCount > 0) {
    visible.push(`${hiddenCount} more object${hiddenCount === 1 ? '' : 's'}`)
  }
  return visible.join(' + ')
}

const describePresentationSelection = (viewer: PresentationViewerState) => {
  const context = viewer.selected_object_context
  let objects: string | undefined
  if (context?.kind === 'table') {
    objects = `Table, ${context.rows}×${context.columns}`
  } else if (context?.kind === 'chart') {
    const title = context.title?.trim()
    objects = title ? `Chart: ${title}` : 'Chart'
    if (context.chart_kind) objects += ` (${context.chart_kind})`
  } else if (viewer.selected_objects?.length) {
    objects = describePresentationObjectGroups(viewer.selected_objects)
  }

  return [objects, describePresentationSlide(viewer)].filter(Boolean).join(', ')
}

/**
 * The selection card an explicit "Add to Chat" builds from the current
 * viewport. The card travels inline in the user message (the agent reads it
 * there); the viewport reminder itself only carries location context.
 */
export const buildViewportSelectionCardContent = (
  context: ViewportContext | null,
): ViewportSelectionCardContent | null => {
  const summary = getViewportSelectionSummary(context)
  if (!summary) return null
  if (context?.detail?.kind !== 'file') return null
  const viewer = context.detail.viewer
  const selectedText =
    viewer && viewer.kind !== 'spreadsheet' ? viewer.selected_text : undefined
  const presentationDescription =
    viewer?.kind === 'presentation'
      ? describePresentationSelection(viewer)
      : undefined

  return {
    title: summary.fileName
      ? `${summary.fileName} — ${summary.label}`
      : summary.label,
    text:
      selectedText ?? presentationDescription ?? `Selected ${summary.label}`,
  }
}

// MARK: Selection lifecycle
//
// One state machine for the pill, the ⌘L shortcut, the composer chips and
// the send path:
//
//   none ──select──▶ selected ──add (pill/⌘L)──▶ staged
//   selected ◀──chip removed / selection changed── staged
//   any ──in-viewer collapse──▶ none
//   staged ──send consumes card──▶ none (clearViewportSelection)
//
// The pill renders only in `selected`; adds no-op in `staged`; the viewer
// selection itself stays alive until a send consumes the card.

export type ViewportSelectionStage = 'none' | 'selected' | 'staged'

type ChatEnvironmentState = ReturnType<ChatEnvironmentStore['getState']>

/**
 * The active tab's live selection as card content. The send/add path reads
 * the synchronous viewer bridge; indicators read the store copy, which
 * trails the bridge by at most a throttle tick.
 */
const getTabSelectionContent = (
  state: ChatEnvironmentState,
  tab: ChatEnvironmentState['panelTabs'][number],
  {
    synchronous = false,
    sourceViewerOpen = state.sourceViewerState === 'open',
  }: { synchronous?: boolean; sourceViewerOpen?: boolean } = {},
): {
  content: ViewportSelectionCardContent
  context: ViewportContext | null
  tab: ChatEnvironmentState['panelTabs'][number]
  /** The raw panel capture backing `context` (carries the measured boxes). */
  viewerState: ReturnType<typeof readLatestViewerState>
} | null => {
  const viewerState = synchronous
    ? (readLatestViewerState(tab.id) ?? state.viewerStateByTabId[tab.id])
    : state.viewerStateByTabId[tab.id]
  const context = getViewportContextFromPanel({
    tab,
    tabs: state.panelTabs,
    sourceViewerOpen,
    viewerState,
  })
  const content = buildViewportSelectionCardContent(context)
  if (!content) return null
  return { content, context, tab, viewerState }
}

const getActiveSelectionContent = (
  state: ChatEnvironmentState,
  options: { synchronous?: boolean } = {},
) => {
  const tab = state.activeTabId
    ? state.panelTabs.find((t) => t.id === state.activeTabId)
    : null
  return tab ? getTabSelectionContent(state, tab, options) : null
}

/** Whether this exact selection already lives as a composer card. */
const isSelectionStaged = (
  state: ChatEnvironmentState,
  content: ViewportSelectionCardContent,
  fileId: UUID | undefined,
): boolean =>
  state.pastedTextCards.some(
    (card) =>
      card.source === 'selection' &&
      card.location?.fileId === fileId &&
      card.title === content.title &&
      card.text === content.text,
  )

export const getViewportSelectionStage = (
  state: ChatEnvironmentState,
): ViewportSelectionStage => {
  const active = getActiveSelectionContent(state)
  if (!active) return 'none'
  return isSelectionStaged(state, active.content, active.tab.nav.fileId)
    ? 'staged'
    : 'selected'
}

export interface ViewportSelectionSnapshot {
  card: PastedTextCard
  /**
   * Keeps the selection lit while the keyboard is elsewhere (the composer,
   * the selection bar's field). SuperDoc is the one viewer whose live
   * selection paint dies when it loses the keyboard (ProseMirror hides its
   * selection on blur; every other viewer keeps an overlay, a kept-range
   * repaint, or the browser's inactive selection), so for a Word selection
   * this arms the steady reveal for the captured range - the words are
   * already painted, so the reveal lights them without scrolling, and its
   * own dismissal (clicking back into the document) takes the highlight down
   * again. Elsewhere it does nothing.
   */
  keepLit: () => void
}

/**
 * The active tab's live selection as a selection card - what Add puts in the
 * composer and what the selection bar sends - read from the synchronous
 * viewer bridge; null with nothing selected.
 */
export const snapshotViewportSelection = (
  store: ChatEnvironmentStore,
): ViewportSelectionSnapshot | null => {
  const state = store.getState()
  const active = getActiveSelectionContent(state, { synchronous: true })
  if (!active) return null
  const { content, context, tab, viewerState } = active
  let page: number | undefined
  const viewer =
    context?.detail?.kind === 'file' ? context.detail.viewer : undefined
  const locator = buildSelectionLocator(viewer, viewerState)
  if (viewer?.kind === 'pdf') {
    page = viewer.selection_start_page ?? viewer.page
  } else if (viewer?.kind === 'presentation') {
    for (const group of viewer.selected_objects ?? []) {
      page =
        page === undefined
          ? group.slide_number
          : Math.min(page, group.slide_number)
    }
    page ??= viewer.slide_number
  } else if (viewer?.kind === 'docx') {
    page = viewer.page
  }
  const location =
    tab.nav.view === 'detail' && tab.nav.fileId && tab.nav.fileName
      ? {
          fileId: tab.nav.fileId,
          fileName: tab.nav.fileName,
          namespace: tab.nav.fileNamespace,
          path: tab.nav.filePath,
          ...(page !== undefined ? { page } : {}),
          ...(locator ? { locator } : {}),
        }
      : undefined
  const keepLit = () => {
    if (viewer?.kind !== 'docx' || !locator || !location) return
    store.getState().updateTabNavById(
      tab.id,
      createFileDetailNavState({
        file: {
          id: location.fileId,
          type: tab.nav.view === 'detail' ? tab.nav.fileType : undefined,
          name: location.fileName,
        },
        namespace: location.namespace,
        path: location.path,
        initialLocator: locator,
        initialHighlightText: content.text,
      }),
    )
  }
  return {
    card: {
      id: uuidv4() as UUID,
      source: 'selection',
      title: content.title,
      text: content.text,
      createdAt: new Date().toISOString(),
      ...(location ? { location } : {}),
    },
    keepLit,
  }
}

/**
 * Explicit add-to-chat: snapshots the active tab's viewport selection into a
 * composer selection card. The viewer selection stays alive — the user is
 * still looking at it; it is only cleared when a send actually consumes the
 * card (`clearViewportSelection`). Returns whether a card was added.
 */
export const addViewportSelectionToChat = (
  store: ChatEnvironmentStore,
): boolean => {
  const snapshot = snapshotViewportSelection(store)
  if (!snapshot) return false
  const state = store.getState()
  // `staged` stage: repeated ⌘L / pill presses on a still-live selection
  // must not stack duplicate chips.
  if (isSelectionStaged(state, snapshot.card, snapshot.card.location?.fileId)) {
    return false
  }
  state.addPastedTextCard(snapshot.card)
  // The composer takes the keyboard next; the reader keeps seeing what
  // travels with the card.
  snapshot.keepLit()
  return true
}

/**
 * Deselect the selections a send consumed, in whichever tab holds each:
 * collapse the DOM selection (PDF/HTML/markdown highlights) when the active
 * tab's is among them, strip the captured viewer state (so the next send's
 * viewport context carries location only), and bump the reset nonce so office
 * viewers (spreadsheet range, presentation objects) drop their own selection
 * overlays, which live outside the DOM selection.
 *
 * Called when a send consumes a selection card — not on add-to-chat or when
 * focus moves to the chat, where the user is still looking at the selection.
 */
export const clearViewportSelection = (
  store: ChatEnvironmentStore,
  sentCards: PastedTextCard[],
): void => {
  const state = store.getState()
  const sentTabs = state.panelTabs.filter((tab) => {
    const selection = getTabSelectionContent(state, tab, {
      synchronous: true,
      sourceViewerOpen: true,
    })
    return (
      selection !== null &&
      sentCards.some(
        (card) =>
          card.source === 'selection' &&
          card.location?.fileId === tab.nav.fileId &&
          card.title === selection.content.title &&
          card.text === selection.content.text,
      )
    )
  })
  const activeSent = sentTabs.some((tab) => tab.id === state.activeTabId)
  if (activeSent) window.getSelection()?.removeAllRanges()
  for (const tab of sentTabs) {
    const viewerState =
      readLatestViewerState(tab.id) ?? state.viewerStateByTabId[tab.id]
    if (!viewerState) continue
    const stripped = stripViewerSelection(viewerState)
    writeLatestViewerState(tab.id, stripped)
    state.setViewerStateByTabId(tab.id, stripped)
  }
  if (activeSent) state.bumpViewerSelectionResetNonce()
  const backgroundSent = sentTabs
    .filter((tab) => tab.id !== state.activeTabId)
    .map((tab) => tab.id)
  if (backgroundSent.length > 0) state.markSelectionsStale(backgroundSent)
}
